import Parse from 'parse';
import { CustomUser } from '../classes';

Parse.initialize(
  process.env.REACT_APP_APP_ID,
  process.env.REACT_APP_JAVASCRIPT_KEY,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

// Cache for user data to avoid duplicate API calls
let cachedUserData = null;
let cacheTimestamp = null;
const CACHE_DURATION = 30000; // 30 seconds cache
let fetchPromise = null; // Promise lock to prevent concurrent fetches

export const authProvider = {
  login: async (params) => {
    const { username, password } = params;

    try {
      if (!username || !password) {
        throw new Error('username and password are required.');
      }

      const response = await Parse.Cloud.run('caseInsensitiveLogin', {
        username,
        password
      });

      localStorage.setItem('role', response?.user?.roleName);

      // Cast to CustomUser to access custom methods
      const user = await CustomUser.logIn(response?.user?.username, password);
      user.set('lastLogin', new Date());
      user.set('loginTimes', (user.get('loginTimes') || 0) + 1);
      user.set('isOnline', true);
      await user.save();

      return {
        userBalance: user.get('balance'),
        role: response?.user?.roleName,
        adminOtpRequired: response?.user?.adminOtpRequired === true,
        requiresOtpVerification: response?.user?.requiresOtpVerification === true,
        adminFirstLoginCompleted: response?.user?.adminFirstLoginCompleted === true,
        otpDeliveryEmail: response?.user?.otpDeliveryEmail || null,
        otpExpiresAt: response?.user?.otpExpiresAt || null
      };
    } catch (error) {
      return Promise.reject(error);
    }
  },

  logout: async () => {
    try {
      const user = Parse.User.current();

      if (!user) {
        return Promise.resolve(); // Nothing to do if user is not logged in
      }

      const userId = user.id; // Save before logOut()

      // Clear cache
      cachedUserData = null;
      cacheTimestamp = null;
      fetchPromise = null;

      // Update isOnline flag
      await user.save({ isOnline: false });

      // Clear stored auth data
      localStorage.removeItem('role');
      localStorage.removeItem('_grecaptcha');
      localStorage.removeItem(`Parse/${process.env.REACT_APP_APP_ID}/sessionToken`);
      localStorage.removeItem(`Parse/${process.env.REACT_APP_APP_ID}/currentUser`);
      localStorage.removeItem(`Parse/${process.env.REACT_APP_APP_ID}/currentInstallationId`);
      await Parse.User.logOut();
      window.location.replace('/login');

      try {
        await Parse.Cloud.run('logoutAllSessions', { userId });
      } catch (cloudError) {
        // Silent error handling
      }

      return Promise.resolve();
    } catch (error) {
      throw Error(error.toString());
    }
  },

  checkAuth: async () => {
    const currentUser = Parse.User.current();
    if (currentUser) {
      try {
        await currentUser.fetch();
        if (currentUser.get('isActive') === false) {
          cachedUserData = null;
          cacheTimestamp = null;
          fetchPromise = null;
          localStorage.clear();
          await Parse.User.logOut();
          return Promise.reject(new Error('Your account is suspended'));
        }

        // Check if we have valid cached data
        const now = Date.now();
        if (cachedUserData && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
          return Promise.resolve();
        }

        // If a fetch is already in progress, wait for it
        if (fetchPromise) {
          await fetchPromise;
          return Promise.resolve();
        }

        // Create fetch promise and store it
        fetchPromise = (async () => {
          try {
            // Direct REST API call to ensure the request is made
            const sessionToken = currentUser.getSessionToken();
            const response = await fetch(
              `${process.env.REACT_APP_SERVER_URL}/classes/_User/${currentUser.id}`,
              {
                method: 'GET',
                headers: {
                  'X-Parse-Application-Id': process.env.REACT_APP_APP_ID,
                  'X-Parse-Session-Token': sessionToken,
                  'Content-Type': 'application/json'
                }
              }
            );

            if (!response.ok) {
              const errorData = await response.json();

              // Check for invalid session token (code 209)
              if (errorData.code === 209 || errorData.error?.includes('Invalid session token')) {
                cachedUserData = null;
                cacheTimestamp = null;
                fetchPromise = null;
                localStorage.clear();
                await Parse.User.logOut();
                throw new Error('Session expired');
              }

              throw new Error(errorData.error || 'Failed to fetch user');
            }

            const userData = await response.json();

            // Cache the user data
            cachedUserData = userData;
            cacheTimestamp = Date.now();

            // Store role name if available (role is already in localStorage from login)
            const existingRole = localStorage.getItem('role');
            if (existingRole) {
              cachedUserData.roleName = existingRole;
            }

            cachedUserData.isActive = userData.isActive;
          } finally {
            // Clear the fetch promise
            fetchPromise = null;
          }
        })();

        await fetchPromise;
        return Promise.resolve();
      } catch (error) {
        // If error code is 209 (Invalid session token), logout
        if (error.code === 209 || error.message?.includes('Invalid session token')) {
          cachedUserData = null;
          cacheTimestamp = null;
          fetchPromise = null;
          localStorage.clear();
          await Parse.User.logOut();
          return Promise.reject(new Error('Session expired'));
        }

        // For other errors, still allow if user exists locally
        return Promise.resolve();
      }
    }
    return Promise.reject();
  },

  checkError: async (error) => {
    const status = error.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem('role');
      localStorage.removeItem('isAuthenticated');
      return Promise.reject();
    }
    return Promise.resolve();
  },

  getPermissions: async () => {
    const role = localStorage.getItem('role');
    return role ? Promise.resolve(role) : Promise.reject();
  },

  getIdentity: async () => {
    const currentUser = Parse.User.current();
    if (currentUser) {
      // Use cached data if available
      if (cachedUserData) {
        const liveAdminOtpStatus = currentUser.get('adminFirstLoginCompleted');
        const liveOtpRequired = currentUser.get('adminOtpRequired');
        const liveIsActive = currentUser.get('isActive');
        const cacheMatchesStatus =
          cachedUserData.adminFirstLoginCompleted === liveAdminOtpStatus &&
          cachedUserData.adminOtpRequired === liveOtpRequired &&
          cachedUserData.isActive === liveIsActive;

        if (!cacheMatchesStatus) {
          return Promise.resolve({
            id: currentUser.id,
            fullName: currentUser.get('username'),
            avatar: currentUser.get('avatar'),
            role: localStorage.getItem('role'),
            email: currentUser.get('email'),
            isActive: liveIsActive,
            adminOtpRequired: liveOtpRequired,
            adminFirstLoginCompleted: liveAdminOtpStatus
          });
        }

        const role = cachedUserData.roleName || localStorage.getItem('role');
        return Promise.resolve({
          id: cachedUserData.objectId || currentUser.id,
          fullName: cachedUserData.username || currentUser.get('username'),
          avatar: cachedUserData.avatar,
          role: role,
          balance: cachedUserData.balance,
          email: cachedUserData.email || cachedUserData.publicEmail || currentUser.get('email'),
          phone: cachedUserData.phone,
          isActive: cachedUserData.isActive,
          adminOtpRequired: cachedUserData.adminOtpRequired,
          adminFirstLoginCompleted: cachedUserData.adminFirstLoginCompleted
        });
      }

      // Fallback to local Parse.User data (no API call)
      const role = localStorage.getItem('role');
      return Promise.resolve({
        id: currentUser.id,
        fullName: currentUser.get('username'),
        avatar: currentUser.get('avatar'),
        role: role,
        email: currentUser.get('email'),
        isActive: currentUser.get('isActive'),
        adminOtpRequired: currentUser.get('adminOtpRequired'),
        adminFirstLoginCompleted: currentUser.get('adminFirstLoginCompleted')
      });
    }
    return Promise.reject();
  }
};
